import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  input,
  model,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormGroup,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { PinnaklIconComponent } from '@pinnakl/shared/ui/icons';

import {
  InputContainerComponent,
  InputValidationComponent,
  LabelContentComponent,
  PrimeButtonComponent,
  PrimeInputPasswordComponent,
} from '@pinnakl/shared/ui/prime';
interface PasswordRequirement {
  rule: string;
  label: string;
  errorName: PasswordErrorName;
}

enum PasswordErrorName {
  LowerCase = 'lowercase',
  UpperCase = 'uppercase',
  Digit = 'digit',
  Symbol = 'symbol',
  Length = 'length',
  Match = 'match',
}

const PASSWORD_MIN_LENGTH = 8;

@Component({
  selector: 'change-password-form',
  templateUrl: './change-password-form.component.html',
  host: {
    '(focusout)': 'onTouched()',
  },
  imports: [
    PinnaklIconComponent,
    InputContainerComponent,
    LabelContentComponent,
    PrimeInputPasswordComponent,
    InputValidationComponent,
    NgClass,
    ReactiveFormsModule,
    PrimeButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ChangePasswordFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: ChangePasswordFormComponent,
      multi: true,
    },
  ],
})
export class ChangePasswordFormComponent implements Validator {
  readonly hideSubmit = input(false);
  readonly isSubmitted = model(false);
  readonly submitted = output<{
    password: string;
  }>();
  onChange: (value: string) => void;
  onTouched: () => void = () => undefined;
  form: UntypedFormGroup;
  passwordRequirements: PasswordRequirement[] = [
    {
      rule: 'oneLowerCase',
      label: 'One lowercase character',
      errorName: PasswordErrorName.LowerCase,
    },
    {
      rule: 'oneUpperCase',
      label: 'One uppercase character',
      errorName: PasswordErrorName.UpperCase,
    },
    {
      rule: 'oneNumber',
      label: 'One number',
      errorName: PasswordErrorName.Digit,
    },
    {
      rule: 'oneSpecialCharacter',
      label: 'One special character',
      errorName: PasswordErrorName.Symbol,
    },
    {
      rule: 'minLength',
      label: 'Minimum 8 characters',
      errorName: PasswordErrorName.Length,
    },
    {
      rule: 'match',
      label: 'Same Password',
      errorName: PasswordErrorName.Match,
    },
  ];
  private fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.form = this.fb.group(
      {
        password: [
          '',
          [
            Validators.required,
            this.lowerCaseValidator,
            this.upperCaseValidator,
            this.digitValidator,
            this.symbolValidator,
            this.lengthValidator,
          ],
        ],
        confirmation: ['', [Validators.required]],
      },
      { validators: this.passwordMatchesValidator },
    );
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ password }) => {
      this.onChange?.(this.form.valid ? password : null);
    });
  }

  get password(): FormControl {
    return this.form.get('password') as FormControl;
  }

  lowerCaseValidator(control: AbstractControl): ValidationErrors | null {
    return control.value && /[a-z]/.test(control.value) ? null : { [PasswordErrorName.LowerCase]: true };
  }

  upperCaseValidator(control: AbstractControl): ValidationErrors | null {
    return /[A-Z]/.test(control.value) ? null : { [PasswordErrorName.UpperCase]: true };
  }

  digitValidator(control: AbstractControl): ValidationErrors | null {
    return /[0-9]/.test(control.value) ? null : { [PasswordErrorName.Digit]: true };
  }

  symbolValidator(control: AbstractControl): ValidationErrors | null {
    return /[!@#$%^&*]/.test(control.value) ? null : { [PasswordErrorName.Symbol]: true };
  }

  lengthValidator(control: AbstractControl): ValidationErrors | null {
    return control.value?.length >= PASSWORD_MIN_LENGTH ? null : { [PasswordErrorName.Length]: true };
  }

  passwordMatchesValidator(control: AbstractControl): ValidationErrors | null {
    const form = control as UntypedFormGroup;
    const { password, confirmation } = form.getRawValue();
    if (password === confirmation) return null;
    return { dontMatch: true };
  }

  changePassword(): void {
    this.isSubmitted.set(true);
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.form.markAsDirty();
      Object.values(this.form.controls).forEach(control => {
        control.markAsDirty();
        control.markAsTouched();
      });
      return;
    }
    const { password } = this.form.getRawValue();
    this.submitted.emit({ password });
    this.form.reset();
    this.isSubmitted.set(false);
  }

  hasError(errorName: PasswordErrorName): boolean {
    if (errorName === PasswordErrorName.Match) {
      return !!this.form.errors?.['dontMatch'];
    }
    return this.password?.errors && this.password.errors[errorName];
  }

  writeValue(password: string): void {
    if (password) {
      this.isSubmitted.set(false);
      this.form.patchValue({
        password,
        confirmation: null,
      });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (this.form.errors) return this.form.errors;
    if (this.password.errors) return this.password.errors;
    return null;
  }
}
